/**
 * INS-37 — redeeming an authority's programme invite.
 *
 * The link is `https://{hostname}/invite?kind=programme&token=…`. `kind`
 * is what keeps it off the existing portal-invite path (`/invite?token=`).
 * The token is parked by `captureInviteToken` and must survive the Auth0
 * round trip. It is cleared only when the outcome is terminal: success after
 * the organisation switch, or a link that can never succeed (wrong email,
 * revoked, expired, already used, unknown). Naming a firm or choosing among
 * several organisations does not consume it — the server hasn't either.
 */
import { ApiRequestError, apiRequest } from "./session";
import { clearParkedInvite, peekInviteToken } from "./invite";

export interface ProgrammeRedeemSuccess {
  organisationId: number;
  organisationName: string;
  programmeId: number;
  alreadyRedeemed: boolean;
  organisationCreated: boolean;
}

export type ProgrammeRedeemPhase =
  | "needs-firm"
  | "needs-org"
  | "not-member"
  | "success"
  | "wrong-email"
  | "revoked"
  | "expired"
  | "already-used"
  | "unknown"
  | "retryable";

export interface ProgrammeRedeemAttempt {
  phase: ProgrammeRedeemPhase;
  /** True when the parked token must be forgotten. */
  terminal: boolean;
  result?: ProgrammeRedeemSuccess;
}

export interface ProgrammeRedeemRequest {
  authorization: string;
  token: string;
  organisationId?: number;
  firmName?: string;
}

function messageOf(error: unknown): { code: string; message: string } {
  if (error instanceof ApiRequestError) {
    return { code: (error.code ?? "").toLowerCase(), message: error.message.toLowerCase() };
  }
  if (error instanceof Error) return { code: "", message: error.message.toLowerCase() };
  return { code: "", message: "" };
}

/**
 * Every row of the redeem contract. Match the server's words, not just the
 * code: `invalid_argument` covers revoked, expired, already used, unknown
 * and "firm name is required", and those are different screens.
 */
export function classifyProgrammeRedeemFailure(error: unknown): ProgrammeRedeemAttempt {
  const { code, message } = messageOf(error);

  if (message.includes("firm name is required")) {
    return { phase: "needs-firm", terminal: false };
  }
  if (code === "failed_precondition" || message.includes("more than one organisation")) {
    return { phase: "needs-org", terminal: false };
  }
  if (message.includes("not one you belong")) {
    return { phase: "not-member", terminal: false };
  }
  if (code === "permission_denied" && message.includes("different email")) {
    return { phase: "wrong-email", terminal: true };
  }
  if (message.includes("different email")) {
    return { phase: "wrong-email", terminal: true };
  }
  if (message.includes("revoked")) return { phase: "revoked", terminal: true };
  if (message.includes("expired")) return { phase: "expired", terminal: true };
  if (message.includes("already been used") || message.includes("already used")) {
    return { phase: "already-used", terminal: true };
  }
  if (message.includes("unknown invite") || message.includes("invalid or unknown")) {
    return { phase: "unknown", terminal: true };
  }
  return { phase: "retryable", terminal: false };
}

export function parseProgrammeRedeemSuccess(raw: unknown): ProgrammeRedeemSuccess | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const organisationId = Number(rec.organisationId);
  const programmeId = Number(rec.programmeId);
  if (!Number.isFinite(organisationId) || organisationId <= 0) return null;
  if (!Number.isFinite(programmeId) || programmeId <= 0) return null;
  return {
    organisationId,
    organisationName: typeof rec.organisationName === "string" ? rec.organisationName : "",
    programmeId,
    alreadyRedeemed: rec.alreadyRedeemed === true,
    organisationCreated: rec.organisationCreated === true,
  };
}

export async function postProgrammeRedeem(body: ProgrammeRedeemRequest): Promise<ProgrammeRedeemSuccess> {
  const payload: Record<string, unknown> = {
    authorization: body.authorization,
    token: body.token,
  };
  if (body.organisationId != null) payload.organisationId = body.organisationId;
  if (body.firmName?.trim()) payload.firmName = body.firmName.trim();
  const raw = await apiRequest<unknown>("/inspect/programme-invites/redeem", {
    method: "POST",
    body: payload,
  });
  const parsed = parseProgrammeRedeemSuccess(raw);
  if (!parsed) throw new Error("Invite redeem response was empty");
  return parsed;
}

/** The token of the last redeem attempt in this tab: only that invite is cleared on commit. */
let attemptedToken: string | null = null;

/**
 * One redeem attempt against the parked token.
 *
 * Terminal failures clear the token here. Success does NOT — the caller
 * clears it only after the organisation switch lands, so a failed switch can
 * retry (the same user redeeming again is a no-op on the server).
 */
export async function runProgrammeRedeemAttempt(input: {
  authorization: string;
  organisationId?: number;
  firmName?: string;
  post?: (body: ProgrammeRedeemRequest) => Promise<ProgrammeRedeemSuccess>;
}): Promise<ProgrammeRedeemAttempt> {
  const token = peekInviteToken();
  if (!token) return { phase: "unknown", terminal: true };
  attemptedToken = token;

  const post = input.post ?? postProgrammeRedeem;
  try {
    const result = await post({
      authorization: input.authorization,
      token,
      organisationId: input.organisationId,
      firmName: input.firmName,
    });
    return { phase: "success", terminal: false, result };
  } catch (error) {
    const failure = classifyProgrammeRedeemFailure(error);
    if (failure.terminal) clearParkedInvite(token);
    return failure;
  }
}

/** Call after the organisation switch succeeded. */
export function commitRedeemedInvite(): void {
  clearParkedInvite(attemptedToken);
  attemptedToken = null;
}
